import { FormEvent, useRef, useState } from 'react'

import * as api from 'api/api'
import { CatalogItem, CatalogKind, Invitation, ProjectRole, User } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { useConfirm } from 'components/Confirm'
import { Icon } from 'components/Icon'
import { useProject } from 'components/Layout'
import { ProjectForm } from 'components/ProjectForm'
import { Button, Card, EmptyState, ErrorState, IconButton, Modal, SelectField, Spinner, TextField } from 'components/ui'
import { CATALOG_LABELS, ROLE_LABELS, formatDate, formatDateTime } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

const CATALOG_KINDS: CatalogKind[] = ['JOB_TITLE', 'MATERIAL', 'EQUIPMENT', 'TEST_TYPE']
const PROJECT_ROLES: ProjectRole[] = ['SITE_ENGINEER', 'PROJECT_MANAGER', 'SUPERVISOR']

const CatalogEditor = ({ projectId }: { projectId: string }) => {
  const { data, error, loading, reload, setData } = useAsync(() => api.fetchCatalog(projectId), [projectId])
  const [kind, setKind] = useState<CatalogKind>('JOB_TITLE')
  const [label, setLabel] = useState('')
  const [editing, setEditing] = useState<{ id: string; label: string } | null>(null)
  const { busy, run } = useAction()
  const confirm = useConfirm()

  const items = (data ?? []).filter((item) => item.kind === kind).sort((a, b) => a.sortOrder - b.sortOrder)

  const replace = (item: CatalogItem) => setData((current) => current?.map((entry) => (entry.id === item.id ? item : entry)))

  const add = async (event: FormEvent) => {
    event.preventDefault()
    const item = await run('add', () => api.addCatalogItem(projectId, kind, label.trim()), t('تمت الإضافة', 'Added'))
    if (item) {
      setData((current) => [...(current ?? []), item])
      setLabel('')
    }
  }

  const move = async (item: CatalogItem, direction: -1 | 1) => {
    const index = items.indexOf(item)
    const other = items[index + direction]
    if (!other) return
    const [a, b] = await Promise.all([
      run(`mv${item.id}`, () => api.updateCatalogItem(item.id, { sortOrder: other.sortOrder })),
      run(`mv${other.id}`, () => api.updateCatalogItem(other.id, { sortOrder: item.sortOrder })),
    ])
    if (a) replace(a)
    if (b) replace(b)
  }

  const rename = async (event: FormEvent) => {
    event.preventDefault()
    if (!editing) return
    const item = await run('rename', () => api.updateCatalogItem(editing.id, { label: editing.label.trim() }), t('تم التعديل', 'Updated'))
    if (item) {
      replace(item)
      setEditing(null)
    }
  }

  const remove = async (item: CatalogItem) => {
    if (
      !(await confirm(t(`حذف «${item.label}» من القائمة؟ لن تتأثر التقارير السابقة.`, `Delete "${item.label}" from the list? Earlier reports are not affected.`), {
        confirmLabel: t('حذف', 'Delete'),
      }))
    )
      return
    const done = await run(`rm${item.id}`, () => api.removeCatalogItem(item.id), t('تم الحذف', 'Deleted'))
    if (done) setData((current) => current?.filter((entry) => entry.id !== item.id))
  }

  return (
    <Card title={t('القوائم المرجعية', 'Reference lists')} id="catalogs">
      <p className="muted small">
        {t(
          'تظهر هذه القوائم كاقتراحات أثناء تعبئة التقرير. المسميات «الثابتة» تظهر دائماً كصفوف في جدول الكوادر وفق ترتيب القالب.',
          'These lists appear as suggestions while filling in a report. "Fixed" titles always appear as rows in the crew table, in the template order.'
        )}
      </p>
      <div className="segmented" role="group" aria-label={t('نوع القائمة', 'List type')}>
        {CATALOG_KINDS.map((key) => (
          <button key={key} type="button" aria-pressed={kind === key} onClick={() => setKind(key)}>
            {CATALOG_LABELS[key]}
          </button>
        ))}
      </div>
      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <>
          <ul className="catalog">
            {items.map((item, index) => (
              <li key={item.id}>
                <span className="catalog__label">
                  {item.label}
                  {item.isFixed ? <span className="chip">{t('ثابت', 'Fixed')}</span> : null}
                </span>
                <span className="catalog__actions">
                  {kind === 'JOB_TITLE' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      busy={busy === `fx${item.id}`}
                      onClick={async () => {
                        const next = await run(`fx${item.id}`, () => api.updateCatalogItem(item.id, { isFixed: !item.isFixed }))
                        if (next) replace(next)
                      }}
                    >
                      {item.isFixed ? t('إلغاء التثبيت', 'Unpin') : t('تثبيت', 'Pin')}
                    </Button>
                  ) : null}
                  <IconButton icon="chevronRight" label={t('نقل للأعلى', 'Move up')} disabled={index === 0} onClick={() => move(item, -1)} className="rot-90" />
                  <IconButton
                    icon="chevronLeft"
                    label={t('نقل للأسفل', 'Move down')}
                    disabled={index === items.length - 1}
                    onClick={() => move(item, 1)}
                    className="rot-90"
                  />
                  <IconButton icon="edit" label={t(`تعديل ${item.label}`, `Edit ${item.label}`)} onClick={() => setEditing({ id: item.id, label: item.label })} />
                  <IconButton icon="trash" label={t(`حذف ${item.label}`, `Delete ${item.label}`)} onClick={() => remove(item)} disabled={busy === `rm${item.id}`} />
                </span>
              </li>
            ))}
            {!items.length ? <li className="muted">{t('القائمة فارغة', 'The list is empty')}</li> : null}
          </ul>
          <form className="inline-form" onSubmit={add}>
            <TextField label={t(`إضافة إلى ${CATALOG_LABELS[kind]}`, `Add to ${CATALOG_LABELS[kind]}`)} value={label} onChange={(event) => setLabel(event.target.value)} />
            <Button type="submit" variant="primary" icon="plus" busy={busy === 'add'} disabled={!label.trim()}>
              {t('إضافة', 'Add')}
            </Button>
          </form>
        </>
      )}
      {editing ? (
        <Modal title={t('تعديل البند', 'Edit item')} onClose={() => setEditing(null)}>
          <form onSubmit={rename} className="stack">
            <TextField label={t('الاسم', 'Name')} value={editing.label} onChange={(event) => setEditing({ ...editing, label: event.target.value })} />
            <div className="form-actions">
              <Button onClick={() => setEditing(null)}>{t('إلغاء', 'Cancel')}</Button>
              <Button type="submit" variant="primary" busy={busy === 'rename'} disabled={!editing.label.trim()}>
                {t('حفظ', 'Save')}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </Card>
  )
}

const MembersEditor = ({ projectId, isAdmin }: { projectId: string; isAdmin: boolean }) => {
  const members = useAsync(() => api.fetchMembers(projectId), [projectId])
  const users = useAsync(() => (isAdmin ? api.fetchUsers() : Promise.resolve([] as User[])), [isAdmin])
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState<ProjectRole>('SITE_ENGINEER')
  const { busy, run } = useAction()
  const confirm = useConfirm()

  const memberIds = new Set(members.data?.map((member) => member.user.id))
  const candidates = (users.data ?? []).filter((user) => user.isActive && !memberIds.has(user.id))

  const assign = async (targetUserId: string, targetRole: ProjectRole) => {
    const done = await run(`set${targetUserId}`, () => api.setMember(projectId, targetUserId, targetRole), t('تم تحديث العضوية', 'Membership updated'))
    if (done) members.reload()
  }

  return (
    <Card title={t('أعضاء المشروع', 'Project members')} id="members">
      {members.loading && !members.data ? (
        <Spinner />
      ) : members.error ? (
        <ErrorState message={members.error} onRetry={members.reload} />
      ) : !members.data?.length ? (
        <EmptyState title={t('لا يوجد أعضاء', 'No members')} icon="users" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('الاسم', 'Name')}</th>
                <th scope="col">{t('البريد', 'Email')}</th>
                <th scope="col">{t('الدور', 'Role')}</th>
                {isAdmin ? (
                  <th scope="col">
                    <span className="sr-only">{t('إجراءات', 'Actions')}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {members.data.map((member) => (
                <tr key={member.id}>
                  <td>{member.user.name}</td>
                  <td dir="ltr" className="ltr-cell">
                    {member.user.email}
                  </td>
                  <td>
                    {isAdmin ? (
                      <select
                        className="input input--compact"
                        aria-label={t(`دور ${member.user.name}`, `Role for ${member.user.name}`)}
                        value={member.role}
                        disabled={busy === `set${member.user.id}`}
                        onChange={(event) => assign(member.user.id, event.target.value as ProjectRole)}
                      >
                        {PROJECT_ROLES.map((key) => (
                          <option key={key} value={key}>
                            {ROLE_LABELS[key]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      ROLE_LABELS[member.role]
                    )}
                  </td>
                  {isAdmin ? (
                    <td>
                      <IconButton
                        icon="trash"
                        label={t(`إزالة ${member.user.name}`, `Remove ${member.user.name}`)}
                        disabled={busy === `rm${member.user.id}`}
                        onClick={async () => {
                          if (!(await confirm(t(`إزالة ${member.user.name} من المشروع؟`, `Remove ${member.user.name} from the project?`), { confirmLabel: t('إزالة', 'Remove') }))) return
                          const done = await run(`rm${member.user.id}`, () => api.removeMember(projectId, member.user.id), t('تمت الإزالة', 'Removed'))
                          if (done) members.reload()
                        }}
                      />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isAdmin ? (
        <form
          className="inline-form"
          onSubmit={async (event) => {
            event.preventDefault()
            await assign(userId, role)
            setUserId('')
          }}
        >
          <SelectField label={t('المستخدم', 'User')} value={userId} onChange={(event) => setUserId(event.target.value)}>
            <option value="">{t('اختر مستخدماً', 'Choose a user')}</option>
            {candidates.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </SelectField>
          <SelectField label={t('الدور', 'Role')} value={role} onChange={(event) => setRole(event.target.value as ProjectRole)}>
            {PROJECT_ROLES.map((key) => (
              <option key={key} value={key}>
                {ROLE_LABELS[key]}
              </option>
            ))}
          </SelectField>
          <Button type="submit" variant="primary" icon="plus" disabled={!userId} busy={busy === `set${userId}`}>
            {t('إضافة عضو', 'Add member')}
          </Button>
        </form>
      ) : null}
    </Card>
  )
}

const InvitationsEditor = ({ projectId }: { projectId: string }) => {
  const { data, error, loading, reload, setData } = useAsync(() => api.fetchInvitations(projectId), [projectId])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<ProjectRole>('SITE_ENGINEER')
  const [link, setLink] = useState<string | null>(null)
  const { busy, run } = useAction()
  const confirm = useConfirm()

  const linkFor = (invitation: Invitation) => `${window.location.origin}/accept-invite?token=${invitation.token}`

  const invite = async (event: FormEvent) => {
    event.preventDefault()
    const invitation = await run('invite', () => api.createInvitation(projectId, email.trim(), role), t('تم إنشاء الدعوة', 'Invitation created'))
    if (invitation) {
      setData((current) => [invitation, ...(current ?? [])])
      setEmail('')
      setLink(linkFor(invitation))
    }
  }

  const revoke = async (invitation: Invitation) => {
    if (!(await confirm(t(`إلغاء دعوة ${invitation.email}؟`, `Cancel the invitation for ${invitation.email}?`), { confirmLabel: t('إلغاء الدعوة', 'Cancel invitation') }))) return
    const done = await run(`rm${invitation.id}`, () => api.revokeInvitation(invitation.id), t('تم إلغاء الدعوة', 'Invitation cancelled'))
    if (done) setData((current) => current?.filter((item) => item.id !== invitation.id))
  }

  return (
    <Card title={t('الدعوات المعلقة', 'Pending invitations')} id="invitations">
      <p className="muted small">
        {t(
          'لا يوجد إرسال بريد تلقائي بعد — بعد إنشاء الدعوة، انسخ الرابط وشاركه مع المدعو يدوياً. تنتهي الدعوة خلال 7 أيام.',
          'Email is not sent automatically yet. After you create an invitation, copy the link and share it manually. The invitation expires in 7 days.'
        )}
      </p>
      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !data?.length ? (
        <p className="muted small">{t('لا توجد دعوات معلقة', 'No pending invitations')}</p>
      ) : (
        <ul className="catalog">
          {data.map((invitation) => (
            <li key={invitation.id}>
              <span className="catalog__label">
                {invitation.email}
                <span className="chip">{ROLE_LABELS[invitation.role]}</span>
              </span>
              <span className="catalog__actions">
                <Button size="sm" variant="ghost" icon="clipboardCheck" onClick={() => navigator.clipboard?.writeText(linkFor(invitation))}>
                  {t('نسخ الرابط', 'Copy link')}
                </Button>
                <IconButton
                  icon="trash"
                  label={t(`إلغاء دعوة ${invitation.email}`, `Cancel invitation for ${invitation.email}`)}
                  onClick={() => revoke(invitation)}
                  disabled={busy === `rm${invitation.id}`}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
      <form className="inline-form" onSubmit={invite}>
        <TextField label={t('البريد الإلكتروني', 'Email')} type="email" dir="ltr" value={email} onChange={(event) => setEmail(event.target.value)} />
        <SelectField label={t('الدور', 'Role')} value={role} onChange={(event) => setRole(event.target.value as ProjectRole)}>
          {PROJECT_ROLES.map((key) => (
            <option key={key} value={key}>
              {ROLE_LABELS[key]}
            </option>
          ))}
        </SelectField>
        <Button type="submit" variant="primary" icon="send" busy={busy === 'invite'} disabled={!email.trim()}>
          {t('إرسال دعوة', 'Send invitation')}
        </Button>
      </form>
      {link ? (
        <Modal title={t('رابط الدعوة', 'Invitation link')} onClose={() => setLink(null)}>
          <p className="muted small">
            {t(
              'شارك هذا الرابط مع المدعو — لا يظهر مرة أخرى بعد إغلاق هذه النافذة، لكن يمكنك نسخه مجدداً من القائمة أعلاه.',
              'Share this link with the invited person. It will not be shown again after you close this window, but you can copy it again from the list above.'
            )}
          </p>
          <div className="field">
            <label className="field__label" htmlFor="invite-link">
              {t('الرابط', 'Link')}
            </label>
            <input id="invite-link" className="input" dir="ltr" readOnly value={link} onFocus={(event) => event.target.select()} />
          </div>
          <div className="form-actions">
            <Button onClick={() => setLink(null)}>{t('إغلاق', 'Close')}</Button>
            <Button variant="primary" icon="clipboardCheck" onClick={() => navigator.clipboard?.writeText(link)}>
              {t('نسخ', 'Copy')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </Card>
  )
}

const TemplateEditor = () => {
  const { data, error, loading, reload, setData } = useAsync(api.fetchTemplateInfo, [])
  const input = useRef<HTMLInputElement>(null)
  const { busy, run } = useAction()
  const confirm = useConfirm()

  return (
    <Card title={t('قالب التقرير (Excel)', 'Report template (Excel)')} id="template">
      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <div className="stack">
          <p>
            <Icon name="file" />{' '}
            {data?.custom
              ? t(`قالب مخصص · آخر تحديث ${formatDateTime(data.updatedAt)}`, `Custom template · last updated ${formatDateTime(data.updatedAt)}`)
              : t('القالب الافتراضي (القالب الرسمي للتقرير اليومي)', 'Default template (the official daily report template)')}
          </p>
          <p className="muted small">
            {t(
              'يجب أن يحافظ القالب الجديد على نفس تخطيط الخلايا (الترويسة، الكوادر، المواد، الأعمال، الزيارات، الملاحظات). يتم فحص القالب بإنشاء تقرير تجريبي قبل اعتماده.',
              'The new template must keep the same cell layout (header, crew, materials, work, visits, notes). It is checked by creating a trial report before it is accepted.'
            )}
          </p>
          <div className="toolbar">
            <Button icon="download" busy={busy === 'dl'} onClick={() => run('dl', api.downloadTemplate)}>
              {t('تنزيل القالب الحالي', 'Download current template')}
            </Button>
            <Button variant="primary" icon="upload" busy={busy === 'up'} onClick={() => input.current?.click()}>
              {t('رفع قالب جديد', 'Upload a new template')}
            </Button>
            {data?.custom ? (
              <Button
                variant="danger"
                icon="undo"
                busy={busy === 'reset'}
                onClick={async () => {
                  if (!(await confirm(t('استعادة القالب الافتراضي؟', 'Restore the default template?'), { confirmLabel: t('استعادة', 'Restore') }))) return
                  const next = await run('reset', api.resetTemplate, t('تمت استعادة القالب الافتراضي', 'Default template restored'))
                  if (next) setData(next)
                }}
              >
                {t('استعادة الافتراضي', 'Restore default')}
              </Button>
            ) : null}
          </div>
          <input
            ref={input}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={async (event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (!file) return
              const next = await run('up', () => api.uploadTemplate(file), t('تم اعتماد القالب الجديد', 'New template applied'))
              if (next) setData(next)
            }}
          />
        </div>
      )}
    </Card>
  )
}

export const SettingsPage = () => {
  const { project, isManager, reloadProject } = useProject()
  const { user } = useAuth()
  const isAdmin = Boolean(user?.isAdmin)
  const { busy, run } = useAction()

  if (!isManager) return <ForbiddenPage message={t('إعدادات المشروع متاحة لمدير المشروع ومدير النظام فقط.', 'Project settings are available to Project Manager and Admin only.')} />

  return (
    <div className="page">
      <div className="page__head">
        <h1>{t('إعدادات المشروع', 'Project settings')}</h1>
      </div>
      <nav className="section-nav" aria-label={t('أقسام الإعدادات', 'Settings sections')}>
        <a href="#details">{t('بيانات المشروع', 'Project details')}</a>
        <a href="#catalogs">{t('القوائم المرجعية', 'Reference lists')}</a>
        <a href="#members">{t('الأعضاء', 'Members')}</a>
        {isAdmin ? <a href="#invitations">{t('الدعوات', 'Invitations')}</a> : null}
        {isAdmin ? <a href="#template">{t('القالب', 'Template')}</a> : null}
      </nav>

      <Card title={t('بيانات المشروع', 'Project details')} id="details">
        {isAdmin ? (
          <ProjectForm
            key={`${project.id}-${project.startDate}-${project.endDate}`}
            initial={project}
            submitLabel={t('حفظ البيانات', 'Save details')}
            busy={busy === 'project'}
            note={t(
              'تغيير تاريخ النهاية يضيف أو يحذف تقارير الأيام (لا يمكن حذف أيام تحتوي بيانات). الترقيم يبقى متسلسلاً.',
              'Changing the end date adds or removes day reports (days that contain data cannot be removed). Numbering stays in sequence.'
            )}
            onSubmit={async (input) => {
              const done = await run('project', () => api.updateProject(project.id, input), t('تم حفظ بيانات المشروع', 'Project details saved'))
              if (done) reloadProject()
            }}
          />
        ) : (
          <dl className="info-grid">
            <div>
              <dt>{t('اسم المشروع', 'Project name')}</dt>
              <dd>{project.name}</dd>
            </div>
            <div>
              <dt>{t('رقم العطاء', 'Tender number')}</dt>
              <dd>{project.tenderNumber || '—'}</dd>
            </div>
            <div>
              <dt>{t('صاحب العمل', 'Client')}</dt>
              <dd>{project.client || '—'}</dd>
            </div>
            <div>
              <dt>{t('المقاول', 'Contractor')}</dt>
              <dd>{project.contractor || '—'}</dd>
            </div>
            <div>
              <dt>{t('الإشراف', 'Supervisor')}</dt>
              <dd>{project.consultant || '—'}</dd>
            </div>
            <div>
              <dt>{t('المدة', 'Duration')}</dt>
              <dd>
                {formatDate(project.startDate)} – {formatDate(project.endDate)}
              </dd>
            </div>
          </dl>
        )}
      </Card>

      <CatalogEditor projectId={project.id} />
      <MembersEditor projectId={project.id} isAdmin={isAdmin} />
      {isAdmin ? <InvitationsEditor projectId={project.id} /> : null}
      {isAdmin ? <TemplateEditor /> : null}
    </div>
  )
}
